import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

let isRefreshing = false;
let refreshQueue = [];

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

async function attemptRefresh() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;
  try {
    const resp = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
    const newToken = resp.data.token;
    localStorage.setItem("token", newToken);
    // Rotation: if the server issued a new refresh token, store it so the 30-day window resets.
    if (resp.data.refresh_token) localStorage.setItem("refreshToken", resp.data.refresh_token);
    return newToken;
  } catch {
    return null;
  }
}

// Axios response interceptor for automatic token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isRefreshEndpoint = originalRequest?.url?.includes("/auth/refresh");

    // Attempt refresh on ANY 401 (not just "expired" text), but never on the refresh endpoint itself
    // and never on 5xx / network errors — so missing config never triggers a logout.
    if (status === 401 && !originalRequest._retry && !isRefreshEndpoint) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      isRefreshing = true;
      let newToken = null;
      try {
        newToken = await attemptRefresh();
      } finally {
        isRefreshing = false;
      }

      if (newToken) {
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } else {
        processQueue(new Error("Refresh failed"));
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        window.location.reload();
        return Promise.reject(error);
      }
    }

    // If the refresh endpoint itself failed with 401, the refresh token is definitively invalid → force logout.
    if (isRefreshEndpoint && status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      window.location.reload();
    }

    return Promise.reject(error);
  }
);

export async function apiCall(method, endpoint, data = null) {
  try {
    const config = {
      method,
      url: `${API}${endpoint}`,
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      timeout: 20000,
    };
    if (data) config.data = data;
    const response = await axios(config);
    return response.data;
  } catch (error) {
    console.error("API Error:", error.response?.data || error.message);
    throw error;
  }
}
